import {
  Arrow,
  Badge,
  Dropdown,
  LoadingLogo,
  NewComments,
  NoResults,
  Typography
} from 'components';
import { cn } from 'utils/cn';

import { ColorsStatus } from 'constants/status';
import { useTranslations } from 'next-intl';
import { KeysEnum } from 'types/KeysEnum';
import HeaderTaskInProgress from './Header';

interface TaskInfo {
  id?: string;
  readyToReview?: string;
  language?: string;
  quality?: string;
  totalWords?: number;
  translators?: string;
  icon: JSX.Element;
  color: ColorsStatus;
  comments?: number;
  commentsNew?: number;
  relatedRequests?: string[];
  relatedRequestsQnt: string;

  onClick?: () => void;
}

export interface ITasksLanguages {
  rows?: TaskInfo[];
  isLoading?: boolean;
  isFetching?: boolean;
}

const TasksInProgress = ({ rows, isLoading, isFetching }: ITasksLanguages) => {
  const t = useTranslations();

  const handleComments = (item: TaskInfo) => () => {
    window.location.href = `/translation/show-task/${item.id}#${KeysEnum.Comments}`;
  };

  return (
    <div>
      <table className="shadow-custom-card w-full bg-white pr-2  ">
        <HeaderTaskInProgress />
        {!!rows?.length && !isLoading && (
          <tbody>
            {rows?.map((item, index) => {
              const firstRelatedTask = item.relatedRequests?.at(0);

              return (
                <tr
                  className={cn(
                    index % 2 === 0 ? 'bg-white' : 'bg-tertiary-900',
                    'cursor-pointer',
                    'hover:bg-tertiary-600'
                  )}
                  key={item.id}
                  onClick={item.onClick}
                >
                  <td className="py-4 pl-3">
                    {/* TODO: ADD POPOVER LIKE IN THE LEGACY */}
                    <Badge
                      icon={item.icon}
                      color={item.color}
                      href={`/translation/show-task/${item.id}`}
                      transparent
                    >
                      #{item.id}
                    </Badge>
                  </td>
                  <td className="break-words  py-4 pl-3 text-left">
                    {item.readyToReview ? (
                      <Typography variant="medium4" className="text-tertiary-100">
                        {item.readyToReview}
                      </Typography>
                    ) : (
                      <Typography variant="light4" className="text-tertiary-300">
                        {t('tasks_table.not_ready')}
                      </Typography>
                    )}
                  </td>
                  <td className="py-4 pl-3 text-left">
                    <Typography variant="medium4" noWrap className="text-tertiary-100">
                      {item.language}
                    </Typography>
                  </td>
                  <td className="py-4 pl-3 text-left">
                    <Typography variant="medium4" className=" text-tertiary-100">
                      {item.quality}
                    </Typography>
                  </td>
                  <td className="py-4 pl-3 text-left">
                    <Typography variant="medium4" className=" text-tertiary-100">
                      {item.totalWords}
                    </Typography>
                  </td>
                  <td className="px-3 py-4 text-left">
                    {item.translators ? (
                      <Typography variant="medium4" className="text-tertiary-100">
                        {item.translators}
                      </Typography>
                    ) : (
                      <Typography variant="light4" className="text-tertiary-300">
                        {t('tasks_table.none_assigned')}
                      </Typography>
                    )}
                  </td>
                  <td className="ml-2 py-4 text-left">
                    <div className="flex flex-row gap-3">
                      <Typography variant="medium4" className="w-6  text-left text-tertiary-100">
                        {item.comments}
                      </Typography>
                      {!!item.commentsNew && (
                        <span className="cursor-pointer font-medium" onClick={handleComments(item)}>
                          <NewComments count={item.commentsNew} />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 pl-3 text-left">
                    <span className="flex items-center gap-4">
                      <span className="my-2">
                        <Badge
                          icon={item.icon}
                          color={item.color}
                          href={`/translation/show-translation/${firstRelatedTask}`}
                        >
                          #{firstRelatedTask}
                        </Badge>
                      </span>
                      <span>
                        <Dropdown
                          noHighlight
                          itemsRow
                          arrow
                          hidden={!(item.relatedRequests && item.relatedRequests?.length > 1)}
                          className={cn('right-[-14px] mt-4')}
                          items={item.relatedRequests?.map((relatedTask) => ({
                            content: (
                              <Badge
                                key={relatedTask}
                                icon={item.icon}
                                color={item.color}
                                href={`/translation/show-translation/${relatedTask}`}
                              >
                                #{relatedTask}
                              </Badge>
                            )
                          }))}
                          header={
                            <Typography variant="medium4" className="pl-2 pt-2 text-tertiary-100">
                              {item.relatedRequestsQnt}
                            </Typography>
                          }
                        >
                          <Arrow className="fill-black" />
                        </Dropdown>
                      </span>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        )}
      </table>
      {((!rows?.length && !isFetching) || isLoading) && (
        <div className="flex h-[60vh] w-full flex-1 flex-col items-center justify-center">
          <NoResults className="!h-full">
            {isLoading ? (
              <LoadingLogo size="small" show />
            ) : (
              <>{t('components.no_results_found')}</>
            )}
          </NoResults>
        </div>
      )}
    </div>
  );
};

export default TasksInProgress;
